import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  notification,
  Row,
  Select,
} from 'antd';
import { ManOutlined, WomanOutlined } from '@ant-design/icons';
import { AxiosResponse } from 'axios';
import moment from 'moment/moment';
import { useTranslation } from 'react-i18next';
import useFetch from '../../../../../hooks/useFetch';
import { FetchApiInterface } from '../../../../../interfaces/FetchApi.interface';
import { ResponseInterface } from '../../../../../interfaces/Response.interface';
import useMessageUser from '../../../../../hooks/useMessageUser';


interface CompromisoDetalle6Props {
  item: any;
  onSaved?: () => void;
}

const CompromisoDetalle6: React.FC<CompromisoDetalle6Props> = ({ item, onSaved }) => {
  const [form] = Form.useForm();
  const { fetchApi, loading }       = useFetch();
  const { showMessage }             = useMessageUser();
  const { t }                       = useTranslation();
  const [processQualities, setProcessQualities]         = useState<any[]>([]);
  const [inappropriateContent, setInappropriateContent] = useState<any[]>([]);

  useEffect(() => {
    inappropriate();
    process_();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!item) return;
    form.resetFields();
    form.setFieldsValue({
      conReadDate:          item?.conReadDate         ? moment(item.conReadDate)  : moment(),
      newDate:              item?.newDate             ? moment(item.newDate)      : undefined,
      conIncludePhoto:      item?.conIncludePhoto     ?? false,
      conAskQuestions:      item?.conAskQuestions     ?? false,
      conPersonalLife:      item?.conPersonalLife     ?? false,
      conExplainDraw:       item?.conExplainDraw      ?? false,
      conScFeelings:        item?.conScFeelings       ?? false,
      conPhoto:             item?.conPhoto            ?? false,
      conLetter:            item?.conLetter           ?? false,
      conFingerprint:       item?.conFingerprint      ?? false,
      conScLessFiveYears:   item?.conScLessFiveYears  ?? false,
      inappropriateContent: item?.icoId              ?? 1,
      processQualities:     item?.pquId              ?? 1,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  const inappropriate = () => {
    const cached = sessionStorage.getItem('cat_inappropriate_content');
    if (cached) { setInappropriateContent(JSON.parse(cached)); return; }
    const dataSend: FetchApiInterface = { url: '/comms/inappropriate-content', method: 'GET' };
    fetchApi(dataSend).then(({ data }: AxiosResponse<ResponseInterface>) => {
      if (data.status === 'success') {
        sessionStorage.setItem('cat_inappropriate_content', JSON.stringify(data?.data || []));
        setInappropriateContent(data?.data || []);
      }
    }).catch(() => {});
  };

  const process_ = () => {
    const cached = sessionStorage.getItem('cat_process_qualities');
    if (cached) { setProcessQualities(JSON.parse(cached)); return; }
    const dataSend: FetchApiInterface = { url: '/comms/process-qualities', method: 'GET' };
    fetchApi(dataSend).then(({ data }: AxiosResponse<ResponseInterface>) => {
      if (data.status === 'success') {
        sessionStorage.setItem('cat_process_qualities', JSON.stringify(data?.data || []));
        setProcessQualities(data?.data || []);
      }
    }).catch(() => {});
  };

  const disableNonTodayDates = (current: any) => current && !current.isSame(moment().startOf('day'), 'day');

  const onFinish = (values: any) => {
    if (!values.conPhoto && !values.conLetter && !values.conFingerprint && !values.conScLessFiveYears) {
      notification.error({
        message: 'Error',
        description: (<div><p>{t('titleOptionRequired')}</p><ul><li>{t('requiredOptionParticipation')} {t('scParticipation')}</li></ul></div>),
      });
      return;
    }
    if (!values.conIncludePhoto && !values.conAskQuestions && !values.conPersonalLife && !values.conExplainDraw && !values.conScFeelings) {
      notification.error({
        message: 'Error',
        description: (<div><p>{t('titleOptionRequired')}</p><ul><li>{t('requiredOptionParticipation')} {t('valuableDialogue')}</li></ul></div>),
      });
      return;
    }

    const payload = {
      conAbsent: item?.conAbsent || false,
      conMakeSpQuestions: false, conIncludePhoto: false, conPersonalLife: false,
      conAskQuestions: false, conDraw: false, conScFeelings: false,
      conExplainDraw: false, conActivities: false,
      ...values,
      scNumber: item?.ascScNumber,
      assId:    item?.assignedId,
      conReadDate: values.conReadDate ? moment(values.conReadDate).format('YYYY-MM-DD') : undefined,
    };
    delete payload.newDate;

    const dataSend: FetchApiInterface = { url: '/comms/control', data: payload };
    fetchApi(dataSend).then(({ data }: AxiosResponse<ResponseInterface>) => {
      if (data.status === 'success') {
        showMessage(data?.message, 'success');
        onSaved?.();
      } else {
        showMessage(data?.message, 'info');
      }
    }).catch((reason: any) => {
      const dataReason = reason?.data || {};
      notification.error({
        message: 'Error',
        description: (
          <div>
            <p>Por favor revisar los siguientes errores:</p>
            <ul>{Object.keys(dataReason).map((key: any) => <li key={key}>{key}: {dataReason[key]}</li>)}</ul>
          </div>
        ),
      });
      showMessage(reason?.message, 'error');
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, minHeight: 800 }}>

      <Form form={form} layout="vertical" onFinish={onFinish} style={{ flex: 1 }}>
        <Row gutter={[20, 20]}>

          {/* Left: SC info card */}
          <Col span={24} md={8}>
            <Card style={{ borderRadius: 10 }}>
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                {item?.ascScGenre === 'Female'
                  ? <WomanOutlined style={{ fontSize: 80 }} />
                  : <ManOutlined   style={{ fontSize: 80 }} />}
                <h2>{item?.ascScName}</h2>
                <h3>{item?.age ?? '-'} {t('yearsOld')}</h3>
                <Divider />
              </div>
              <p><strong>{t('scNumber')}:</strong> {item?.ascScNumber}</p>
              <p><strong>{t('no')}:</strong> {item?.assNo ?? '-'}</p>
              <p><strong>{t('sp')}:</strong> {item?.assSpNumber}</p>
              <p><strong>{t('spName')}:</strong> {item?.assSpName}</p>
              <p><strong>{t('type')}:</strong> {item?.assComType}</p>
              <p><strong>{t('obs')}:</strong> {item?.assObs ?? '-'}</p>
              <p><strong>{t('pu')}:</strong> {item?.ascPuCode ?? '-'}</p>
              <p><strong>{t('community')}:</strong> {item?.ascLevel ?? '-'}</p>
              <p><strong>{t('fdc')}:</strong> {item?.ascCwCode ? `${item.ascCwCode} - ${item.ascCw}` : '-'}</p>
              {item?.meetingReason && <p><strong>{t('meetingReason')}:</strong> {item.meetingReason}</p>}
              <Divider />
              <p><strong>{t('modificationDate')}:</strong> {item?.conReadDate ?? '-'}</p>
            </Card>
          </Col>

          {/* Right: form fields */}
          <Col span={24} md={16}>
            <Card style={{ borderRadius: 10 }}>
              <Row gutter={[10, 10]}>
                <Col span={24}>
                  <Row gutter={[10, 10]}>
                    <Col span={24} md={12}>
                      <Form.Item
                        name="conReadDate"
                        label={t('readDate')}
                        rules={[{ required: true, message: t('selectDate') }]}
                        initialValue={moment()}
                      >
                        <DatePicker className="w-100" disabledDate={disableNonTodayDates} format="DD-MM-YYYY" />
                      </Form.Item>
                    </Col>
                    <Col span={24} md={12}>
                      <Form.Item
                        name="newDate"
                        label={t('childDataDueDate')}
                        rules={[{ required: true, message: t('selectDate') }]}
                      >
                        <DatePicker className="w-100" disabled format="DD-MM-YYYY" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Divider />
                </Col>

                <Col span={24} md={12}>
                  <p><strong><span className="text-danger">* </span>{t('valuableDialogue')}:</strong></p>
                  <Form.Item name="conIncludePhoto"  valuePropName="checked"><Checkbox>{t('includePhoto')}</Checkbox></Form.Item>
                  <Form.Item name="conAskQuestions"  valuePropName="checked"><Checkbox>{t('answersQuestions')}</Checkbox></Form.Item>
                  <Form.Item name="conPersonalLife"  valuePropName="checked"><Checkbox>{t('talksAboutPersonalLife')}</Checkbox></Form.Item>
                  <Form.Item name="conExplainDraw"   valuePropName="checked"><Checkbox>{t('explainsDrawing')}</Checkbox></Form.Item>
                  <Form.Item name="conScFeelings"    valuePropName="checked"><Checkbox>{t('expressesFeelings')}</Checkbox></Form.Item>
                </Col>

                <Col span={24} md={12}>
                  <Form.Item rules={[{ required: true, message: t('codeRequired') }]} label={t('inappropriateContent')} name="inappropriateContent">
                    <Select>
                      {inappropriateContent.map((it: any, i: number) => (
                        <Select.Option key={i} value={it?.icoId}>{t(it?.icoText)}</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Divider />
                  <p><strong><span className="text-danger">* </span>{t('scParticipation')}:</strong></p>
                  <Form.Item name="conPhoto"           valuePropName="checked"><Checkbox>{t('photo')}</Checkbox></Form.Item>
                  <Form.Item name="conLetter"          valuePropName="checked"><Checkbox>{t('letter')}</Checkbox></Form.Item>
                  <Form.Item name="conFingerprint"     valuePropName="checked"><Checkbox>{t('fingerprint')}</Checkbox></Form.Item>
                  <Form.Item name="conScLessFiveYears" valuePropName="checked"><Checkbox>{t('scUnderFiveYears')}</Checkbox></Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item rules={[{ required: true, message: t('codeRequired') }]} label={t('processQuality')} name="processQualities">
                    <Select>
                      {processQualities.map((it: any, i: number) => (
                        <Select.Option key={i} value={it?.pquId}>{t(it?.pquText)}</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>

        </Row>
      </Form>

      {/* Action bar */}
      <div style={{
        borderTop: '1px solid #e8e8e8',
        paddingTop: 16,
        marginTop: 16,
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 8,
      }}>
        <Button type="primary" shape="round" loading={loading} onClick={() => form.submit()}>
          {t('save')}
        </Button>
      </div>

    </div>
  );
};

export default CompromisoDetalle6;
